import { ScrollingModule } from '@angular/cdk/scrolling';
import { Component, DestroyRef, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { RvDividerComponent } from '../../divider/divider.component';
import { MatTabsModule } from '@angular/material/tabs';
import { UIRouterModule } from '@uirouter/angular';
import { FoldersApiService } from 'src/app/api/services/folders-api.service';
import { FolderPathComponent } from 'src/app/components/folders/folder-path/folder-path.component';
import { RvSpinnerDirective } from 'src/app/components/loading/rv-spinner.directive';
import { SearchFilterComponent } from 'src/app/components/search-filter/search-filter.component';
import { FolderService } from 'src/app/shared/services/folder.service';
import { PermissionService } from 'src/app/auth/services/permission.service';
import {
  PageWithListComponent,
  ScrollingList,
} from '../../scrolling-list/legacy/page-with-list.component';
import { MoveFolderDialogService } from '../services/move-folder-dialog.service';
import { AccessibilityModule } from 'src/app/accessibility';
import { getObjectId } from 'src/app/shared/helpers/object.helper';
import { RvButtonComponent } from '../../button/button.component';

@Component({
  selector: 'item-selector-dialog',
  templateUrl: './item-selector-dialog.component.html',
  styleUrls: ['./item-selector-dialog.component.scss'],
  imports: [
    RvButtonComponent,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTabsModule,
    RvDividerComponent,
    MatCheckboxModule,
    ScrollingModule,
    AccessibilityModule,
    FolderPathComponent,
    SearchFilterComponent,
    RvSpinnerDirective,
    UIRouterModule,
  ],
  providers: [MoveFolderDialogService],
})
export class ItemSelectorDialogComponent<
  T extends { folderId?: string; isSelected?: boolean },
> extends PageWithListComponent<T> {
  protected destroyRef = inject(DestroyRef);
  set itemSelectorService(itemSelectorService: ItemSelectorService<T>) {
    this._itemSelectorService = itemSelectorService;
    this._itemSelectorService.load(
      this.foldersSupported
        ? { folderId: FoldersApiService.ROOT_FOLDER_ID }
        : {},
    );
    if (this.foldersSupported) {
      this.selectFolderDialogService.initTree(this.data.kind);
      this.selectFolderDialogService.updateFolderList({
        kind: this.data.kind,
        folderId: '',
        parentId: '',
      });
    }
  }

  protected _itemSelectorService: ItemSelectorService<T>;

  protected dialogRef = inject(MatDialogRef<ItemSelectorDialogComponent<T>>);
  protected selectFolderDialogService = inject(MoveFolderDialogService);
  protected folderService = inject(FolderService);
  protected permissionService = inject(PermissionService);
  protected data: {
    kind: 'Display' | 'Schedule' | 'Presentation';
    multiple: boolean;
    canAdd?: boolean;
  } = inject(MAT_DIALOG_DATA);

  protected get canAdd(): boolean {
    return this.data.canAdd !== false;
  }

  protected get foldersSupported(): boolean {
    return this.permissionService.areFoldersSupported(this.data.kind);
  }

  protected currentParentFolderId = '';

  constructor() {
    super();
    this.filterConfig = {
      placeholder: `Search ${this.data.kind}s`,
    };
  }

  filterConfig: { placeholder: string };

  enterFolder(folder: { id?: string }) {
    this.selectFolderDialogService.updateFolderList({
      kind: this.data.kind,
      parentId: folder.id,
      search: undefined,
      folderId: '',
    });
    this._itemSelectorService.load({ folderId: folder.id });
  }

  addItem() {
    this.dialogRef.close(null);
    this._itemSelectorService.addItem();
  }

  selectItem(item: any, inSelectedItems?: boolean) {
    this._itemSelectorService.selectItem(item, inSelectedItems);
    if (!this.data.multiple) {
      this.dialogRef.close(item);
    }
  }

  protected confirmSelection() {
    this.dialogRef.close(this.items.filter((item) => item.isSelected));
  }

  get items() {
    const list =
      this._itemSelectorService?.getScrollingList()?.items.list || [];

    if (!this.foldersSupported) {
      return list;
    }

    const folders = this._itemSelectorService.search.query
      ? []
      : this.selectFolderDialogService.folders();
    const items = list.filter((item) =>
      this.folderService.showFolderItem(
        this.selectFolderDialogService.currentParentFolderId ||
          FoldersApiService.ROOT_FOLDER_ID,
        item.folderId,
      ),
    );

    return [...folders, ...items];
  }

  get selectedItems() {
    return this._itemSelectorService.getSelectedItems() || [];
  }

  protected get someItemIsSelected() {
    return this.items.some((item) => item.isSelected);
  }

  protected get someSelectedItemIsUnselected() {
    return this.selectedItems.some((item) => !item.isSelected);
  }

  getDestroyRef(): DestroyRef {
    return this.destroyRef;
  }

  getScrollingList(): ScrollingList<any> {
    return this._itemSelectorService.getScrollingList();
  }

  getItemId(item: any): string {
    return getObjectId(item);
  }
}

export interface ItemSelectorService<T> {
  addItem(): void;
  getScrollingList(): ScrollingList<T>;
  load(search?: any): void;
  selectItem(item: T, inSelectedItems?: boolean): void;
  isSelected(item: T, inSelectedItems?: boolean): boolean;
  getSelectedItems(): T[];
  search: {
    query?: string;
    folderId?: string;
  };
}
